import ApplicationController from './application_controller';

/**
 * Address Form Controller
 *
 * Adapts address form fields based on selected country:
 * - Shows dropdown for admin area (US/CA/AU) or text input (others)
 * - Makes fields required/optional per country norms
 * - Validates postal code format where applicable
 * - Updates labels to match country terminology
 *
 * Usage:
 *   <div data-controller="address-form"
 *        data-address-form-countries-value='[{"value":"US","label":"United States"},...]'>
 *     <select data-address-form-target="country" data-action="change->address-form#countryChanged">
 *     <div data-address-form-target="adminAreaContainer">...</div>
 *     <input data-address-form-target="postalCode" data-action="blur->address-form#validatePostalCode">
 *   </div>
 *
 * The countries-value can be populated from the backend using pycountry:
 *   from core.services.countries import get_countries_json
 *   context['countries_json'] = get_countries_json()
 *   # Template: data-address-form-countries-value="{{ countries_json }}"
 */

interface CountryOption {
  value: string;
  label: string;
}

interface CountryConfig {
  name: string;
  adminAreaLabel: string;
  adminAreaRequired: boolean;
  adminAreaOptions?: { value: string; label: string }[];
  postalCodeLabel: string;
  postalCodeRequired: boolean;
  postalCodePattern?: RegExp;
  postalCodePlaceholder: string;
}

// Countries with dropdown admin areas (canonical lists)
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  // Territories
  { value: 'AS', label: 'American Samoa' },
  { value: 'GU', label: 'Guam' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'VI', label: 'U.S. Virgin Islands' },
];

const CA_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const AU_STATES = [
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'WA', label: 'Western Australia' },
];

// Country configurations
const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  US: {
    name: 'United States',
    adminAreaLabel: 'State',
    adminAreaRequired: true,
    adminAreaOptions: US_STATES,
    postalCodeLabel: 'ZIP code',
    postalCodeRequired: true,
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodePlaceholder: '12345',
  },
  CA: {
    name: 'Canada',
    adminAreaLabel: 'Province',
    adminAreaRequired: true,
    adminAreaOptions: CA_PROVINCES,
    postalCodeLabel: 'Postal code',
    postalCodeRequired: true,
    postalCodePattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
    postalCodePlaceholder: 'A1A 1A1',
  },
  AU: {
    name: 'Australia',
    adminAreaLabel: 'State/Territory',
    adminAreaRequired: true,
    adminAreaOptions: AU_STATES,
    postalCodeLabel: 'Postcode',
    postalCodeRequired: true,
    postalCodePattern: /^\d{4}$/,
    postalCodePlaceholder: '2000',
  },
  GB: {
    name: 'United Kingdom',
    adminAreaLabel: 'County',
    adminAreaRequired: false,
    postalCodeLabel: 'Postcode',
    postalCodeRequired: true,
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    postalCodePlaceholder: 'SW1A 1AA',
  },
  DE: {
    name: 'Germany',
    adminAreaLabel: 'State',
    adminAreaRequired: false,
    postalCodeLabel: 'Postal code',
    postalCodeRequired: true,
    postalCodePattern: /^\d{5}$/,
    postalCodePlaceholder: '10115',
  },
  FR: {
    name: 'France',
    adminAreaLabel: 'Region',
    adminAreaRequired: false,
    postalCodeLabel: 'Postal code',
    postalCodeRequired: true,
    postalCodePattern: /^\d{5}$/,
    postalCodePlaceholder: '75001',
  },
  // Countries with lenient validation
  RS: {
    name: 'Serbia',
    adminAreaLabel: 'Region',
    adminAreaRequired: false,
    postalCodeLabel: 'Postal code',
    postalCodeRequired: false, // Flexible usage
    postalCodePlaceholder: '11000',
  },
};

// Default config for unlisted countries
const DEFAULT_CONFIG: CountryConfig = {
  name: 'Other',
  adminAreaLabel: 'State / Province / Region',
  adminAreaRequired: false,
  postalCodeLabel: 'Postal code',
  postalCodeRequired: false,
  postalCodePlaceholder: '',
};

// Common countries - shown at top of dropdown for quick access
const COMMON_COUNTRY_CODES = new Set([
  'US', 'CA', 'GB', 'AU', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE',
  'AT', 'CH', 'IE', 'NZ', 'MX', 'BR', 'JP', 'KR', 'SG', 'RS', 'HR', 'SI',
]);

// Fallback countries list - used only if not provided via data attribute
// Prefer using backend pycountry via data-address-form-countries-value
const FALLBACK_COUNTRIES: CountryOption[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'AT', label: 'Austria' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'IE', label: 'Ireland' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'MX', label: 'Mexico' },
  { value: 'BR', label: 'Brazil' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'RS', label: 'Serbia' },
  { value: 'HR', label: 'Croatia' },
  { value: 'SI', label: 'Slovenia' },
];

export default class extends ApplicationController {
  static values = {
    countries: { type: Array, default: [] },
    initialCountry: { type: String, default: '' },
    initialRegion: { type: String, default: '' },
  };
  static targets = [
    'country',
    'adminAreaContainer',
    'adminAreaLabel',
    'adminAreaInput',
    'adminAreaSelect',
    'postalCode',
    'postalCodeLabel',
    'postalCodeError',
  ];

  declare countryTarget: HTMLSelectElement;
  declare adminAreaContainerTarget: HTMLElement;
  declare adminAreaLabelTarget: HTMLLabelElement;
  declare adminAreaInputTarget: HTMLInputElement;
  declare adminAreaSelectTarget: HTMLSelectElement;
  declare postalCodeTarget: HTMLInputElement;
  declare postalCodeLabelTarget: HTMLLabelElement;
  declare postalCodeErrorTarget: HTMLElement;

  declare hasAdminAreaContainerTarget: boolean;
  declare hasAdminAreaLabelTarget: boolean;
  declare hasAdminAreaInputTarget: boolean;
  declare hasAdminAreaSelectTarget: boolean;
  declare hasPostalCodeTarget: boolean;
  declare hasPostalCodeLabelTarget: boolean;
  declare hasPostalCodeErrorTarget: boolean;

  declare countriesValue: CountryOption[];
  declare initialCountryValue: string;
  declare initialRegionValue: string;

  private currentConfig: CountryConfig = DEFAULT_CONFIG;

  /**
   * Get countries list - from value or fallback
   */
  get countries(): CountryOption[] {
    return this.countriesValue.length > 0 ? this.countriesValue : FALLBACK_COUNTRIES;
  }

  connect() {
    // Populate country dropdown if empty (only has placeholder option)
    if (this.countryTarget.options.length <= 1) {
      this.populateCountryDropdown();
    }

    // Pre-select initial country if provided
    if (this.initialCountryValue) {
      this.countryTarget.value = this.initialCountryValue;
    }

    // Initialize based on current country selection (this populates region dropdown)
    this.countryChanged();

    // Pre-select initial region if provided (must happen after countryChanged populates the dropdown)
    if (this.initialRegionValue) {
      this.selectInitialRegion();
    }

    // Disable admin area fields if no country is selected
    // This prevents users from entering data that will be lost when they select a country
    if (!this.countryTarget.value) {
      this.disableAdminAreaFields();
    }
  }

  /**
   * Select the initial region value in the appropriate field (dropdown or input)
   */
  private selectInitialRegion() {
    const config = this.currentConfig;
    const regionValue = this.initialRegionValue;

    if (config.adminAreaOptions && config.adminAreaOptions.length > 0) {
      // Country uses dropdown - try to select by value or label
      if (this.hasAdminAreaSelectTarget) {
        // First try exact value match
        const exactMatch = Array.from(this.adminAreaSelectTarget.options).find(
          (opt) => opt.value === regionValue
        );
        if (exactMatch) {
          this.adminAreaSelectTarget.value = regionValue;
          return;
        }

        // Try matching by label (case-insensitive)
        const labelMatch = Array.from(this.adminAreaSelectTarget.options).find(
          (opt) => opt.textContent?.toLowerCase() === regionValue.toLowerCase()
        );
        if (labelMatch) {
          this.adminAreaSelectTarget.value = labelMatch.value;
          return;
        }

        // Try partial match (region value might be full name, option might be abbreviation)
        const partialMatch = config.adminAreaOptions.find(
          (opt) => opt.label.toLowerCase() === regionValue.toLowerCase()
        );
        if (partialMatch) {
          this.adminAreaSelectTarget.value = partialMatch.value;
        }
      }
    } else {
      // Country uses text input - value should already be set from template
      // but set it explicitly just in case
      if (this.hasAdminAreaInputTarget && !this.adminAreaInputTarget.value) {
        this.adminAreaInputTarget.value = regionValue;
      }
    }
  }

  /**
   * Populate the country dropdown with available countries
   * Common countries appear at the top, followed by a divider, then remaining countries
   */
  private populateCountryDropdown() {
    const select = this.countryTarget;
    const currentValue = select.value;

    // Keep the first option (placeholder) if it exists
    while (select.options.length > 1) {
      select.remove(1);
    }

    // Split countries into common and other
    const allCountries = this.countries;
    const commonCountries: CountryOption[] = [];
    const otherCountries: CountryOption[] = [];

    allCountries.forEach((country) => {
      if (COMMON_COUNTRY_CODES.has(country.value)) {
        commonCountries.push(country);
      } else {
        otherCountries.push(country);
      }
    });

    // Sort common countries by their order in FALLBACK_COUNTRIES for consistency
    const commonOrder = Array.from(COMMON_COUNTRY_CODES);
    commonCountries.sort((a, b) => {
      return commonOrder.indexOf(a.value) - commonOrder.indexOf(b.value);
    });

    // Add common countries first
    commonCountries.forEach((country) => {
      const option = document.createElement('option');
      option.value = country.value;
      option.textContent = country.label;
      select.appendChild(option);
    });

    // Add divider if there are other countries
    if (otherCountries.length > 0 && commonCountries.length > 0) {
      const divider = document.createElement('option');
      divider.disabled = true;
      divider.textContent = '──────────────';
      select.appendChild(divider);
    }

    // Add remaining countries (already alphabetically sorted from backend)
    otherCountries.forEach((country) => {
      const option = document.createElement('option');
      option.value = country.value;
      option.textContent = country.label;
      select.appendChild(option);
    });

    // Restore selection if valid
    if (currentValue) {
      select.value = currentValue;
    }
  }

  /**
   * Handle country selection change
   */
  countryChanged() {
    const countryCode = this.countryTarget.value;
    this.currentConfig = COUNTRY_CONFIGS[countryCode] || DEFAULT_CONFIG;

    // Enable admin area fields now that a country is selected
    if (countryCode) {
      this.enableAdminAreaFields();
    } else {
      this.disableAdminAreaFields();
    }

    this.updateAdminArea();
    this.updatePostalCode();
  }

  /**
   * Update admin area field based on country
   */
  private updateAdminArea() {
    const config = this.currentConfig;

    // Update label
    if (this.hasAdminAreaLabelTarget) {
      const requiredMark = config.adminAreaRequired ? '' : ' <span class="font-normal text-gray-500">(optional)</span>';
      this.adminAreaLabelTarget.innerHTML = config.adminAreaLabel + requiredMark;
    }

    // Show dropdown or text input
    if (config.adminAreaOptions && config.adminAreaOptions.length > 0) {
      // Use dropdown
      if (this.hasAdminAreaInputTarget) {
        this.adminAreaInputTarget.classList.add('hidden');
        this.adminAreaInputTarget.name = '';
      }
      if (this.hasAdminAreaSelectTarget) {
        this.adminAreaSelectTarget.classList.remove('hidden');
        this.adminAreaSelectTarget.name = 'region_name';
        this.adminAreaSelectTarget.required = config.adminAreaRequired;
        this.populateSelect(this.adminAreaSelectTarget, config.adminAreaOptions);
      }
    } else {
      // Use text input
      if (this.hasAdminAreaSelectTarget) {
        this.adminAreaSelectTarget.classList.add('hidden');
        this.adminAreaSelectTarget.name = '';
      }
      if (this.hasAdminAreaInputTarget) {
        this.adminAreaInputTarget.classList.remove('hidden');
        this.adminAreaInputTarget.name = 'region_name';
        this.adminAreaInputTarget.required = config.adminAreaRequired;
        this.adminAreaInputTarget.placeholder = config.adminAreaLabel;
      }
    }
  }

  /**
   * Update postal code field based on country
   */
  private updatePostalCode() {
    const config = this.currentConfig;

    // Update input
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.required = config.postalCodeRequired;
      this.postalCodeTarget.placeholder = config.postalCodePlaceholder;
    }

    // Clear any existing error
    this.clearPostalCodeError();
  }

  /**
   * Validate postal code format on blur
   */
  validatePostalCode() {
    if (!this.hasPostalCodeTarget) return true;

    const value = this.postalCodeTarget.value.trim();
    const config = this.currentConfig;

    // Skip validation if empty and not required
    if (!value && !config.postalCodeRequired) {
      this.clearPostalCodeError();
      return true;
    }

    // Skip format validation if no pattern defined (lenient countries)
    if (!config.postalCodePattern) {
      this.clearPostalCodeError();
      return true;
    }

    // Validate format
    if (!config.postalCodePattern.test(value)) {
      this.showPostalCodeError(`Please enter a valid ${config.postalCodeLabel.toLowerCase()}`);
      return false;
    }

    this.clearPostalCodeError();
    return true;
  }

  /**
   * Populate a select element with options
   */
  private populateSelect(
    select: HTMLSelectElement,
    options: { value: string; label: string }[]
  ) {
    const currentValue = select.value;

    // Clear existing options except placeholder
    while (select.options.length > 1) {
      select.remove(1);
    }

    // Add new options
    options.forEach((opt) => {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label;
      select.appendChild(option);
    });

    // Restore selection if still valid
    if (currentValue) {
      select.value = currentValue;
    }
  }

  /**
   * Show postal code validation error
   */
  private showPostalCodeError(message: string) {
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.classList.add('border-red-500');
      this.postalCodeTarget.classList.remove('border-gray-300');
    }

    if (this.hasPostalCodeErrorTarget) {
      this.postalCodeErrorTarget.textContent = message;
      this.postalCodeErrorTarget.classList.remove('hidden');
    }
  }

  /**
   * Clear postal code validation error
   */
  private clearPostalCodeError() {
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.classList.remove('border-red-500');
      this.postalCodeTarget.classList.add('border-gray-300');
    }

    if (this.hasPostalCodeErrorTarget) {
      this.postalCodeErrorTarget.textContent = '';
      this.postalCodeErrorTarget.classList.add('hidden');
    }
  }

  /**
   * Disable admin area fields to prevent data entry before country selection
   * This prevents users from entering text that will be lost when switching to a dropdown
   */
  private disableAdminAreaFields() {
    if (this.hasAdminAreaInputTarget) {
      this.adminAreaInputTarget.disabled = true;
      this.adminAreaInputTarget.placeholder = 'Select country first';
      this.adminAreaInputTarget.classList.add('bg-gray-100', 'cursor-not-allowed');
    }

    if (this.hasAdminAreaSelectTarget) {
      this.adminAreaSelectTarget.disabled = true;
      this.adminAreaSelectTarget.classList.add('bg-gray-100', 'cursor-not-allowed');
    }
  }

  /**
   * Enable admin area fields after country selection
   */
  private enableAdminAreaFields() {
    if (this.hasAdminAreaInputTarget) {
      this.adminAreaInputTarget.disabled = false;
      this.adminAreaInputTarget.classList.remove('bg-gray-100', 'cursor-not-allowed');
    }

    if (this.hasAdminAreaSelectTarget) {
      this.adminAreaSelectTarget.disabled = false;
      this.adminAreaSelectTarget.classList.remove('bg-gray-100', 'cursor-not-allowed');
    }
  }
}
